import { ComponentType, ReactNode } from 'react';

import { Stack, StackProps, SxProps, styled, Typography } from '@mui/material';

import { ChartTooltipElementProps, ChartTooltipPayload } from './types';
import {
  DataPopper,
  DataPopperProps,
  DataPopperRow,
} from '../../../data-popper';
import { useTooltipConfig } from '../../TooltipConfigProvider';
import { DataKey } from '../../types';
import { tokens } from '../../../../theme/generated/tokens';

const ChartTooltipFooter = styled(Stack, {
  name: 'DsChartTooltip',
  slot: 'Footer',
  target: 'DsChartTooltip-Footer',
  overridesResolver: (_, styles) => {
    return [styles.footer];
  },
})<StackProps>({});
ChartTooltipFooter.displayName = 'ChartTooltipFooter';

const ChartTooltipRoot = styled(DataPopper, {
  name: 'DsChartTooltip',
  slot: 'Root',
  target: 'DsChartTooltip-Root',
  overridesResolver: (_, styles) => {
    return [styles.root];
  },
})<DataPopperProps>({});
ChartTooltipRoot.displayName = 'ChartTooltipRoot';

type ContentRowProps<T, V = string | number> = {
  payload: ChartTooltipPayload<T, V>;
  testId: string;
  formatValue?(value: V | undefined, dataKey: DataKey<T, V>): ReactNode;
};

const ContentRow = <T, V = string | number>({
  payload,
  testId,
  formatValue,
}: ContentRowProps<T, V>) => {
  const {
    name,
    dataKey,
    unit,
    value,
    color: _color,
    stroke,
    strokeOpacity: opacity,
    payload: dataPoint,
    hide,
  } = payload;
  const { getElementConfig } = useTooltipConfig<T>();
  // unit here is an internal element id.
  const config = unit ? getElementConfig(unit) : undefined;

  if (config?.tooltipConfig?.hidden || hide) {
    return null;
  }

  const displayName = config?.tooltipConfig?.displayName ?? name;
  const displayUnit = config?.tooltipConfig?.unit;
  const markerStyles = config?.getMarkerStylesByPoint?.(dataPoint);
  const color = _color === 'none' ? stroke ?? 'none' : _color;

  const formattedValue = formatValue ? formatValue(value, dataKey) : value;

  return (
    <DataPopperRow
      testId={testId}
      indicator={{
        color: markerStyles?.color ?? color,
        variant: config?.indicatorVariant ?? 'outlined',
        sx: {
          opacity: markerStyles?.opacity ?? opacity,
        },
      }}
      left={
        <Typography
          variant="P10R"
          component="span"
          color={tokens.theme.fg.structural.primary}
        >
          {displayName}
        </Typography>
      }
      right={
        <Stack direction="row" gap={4}>
          <Typography variant="P10R" color={tokens.theme.fg.structural.primary}>
            {formattedValue as ReactNode}
          </Typography>
          {displayUnit && (
            <Typography
              variant="P10R"
              color={tokens.theme.fg.structural.secondary}
            >
              {displayUnit}
            </Typography>
          )}
        </Stack>
      }
    />
  );
};

type TooltipContentProps<T, V = string | number> = {
  payload?: readonly ChartTooltipPayload<T, V>[];
  Header?: ComponentType<ChartTooltipElementProps<T>>;
  Footer?: ComponentType<ChartTooltipElementProps<T>>;
  sx?: SxProps;
  testId: string;
  formatValue?(value: V | undefined, dataKey: DataKey<T, V>): ReactNode;
};

export const TooltipContent = <T, V = string | number>({
  payload,
  Header,
  Footer,
  sx,
  testId,
  formatValue,
}: TooltipContentProps<T, V>) => {
  const item = payload?.[0]?.payload;

  return (
    <ChartTooltipRoot size="small" sx={sx} testId={`${testId}-root`}>
      {!!Header && !!item && <Header data={item} testId={`${testId}-header`} />}

      {payload?.map((p, index) => (
        <ContentRow<T, V>
          key={index}
          testId={`${testId}-row-${index}`}
          payload={p}
          formatValue={formatValue}
        />
      ))}

      {!!Footer && !!item && <Footer data={item} testId={`${testId}-footer`} />}
    </ChartTooltipRoot>
  );
};
